class VideoPlayer {
  static instances = []; // Track all video player instances

  constructor(container) {
    // Cache elements
    this.container = container;
    this.previewVideo = container.querySelector(".preview-video");
    this.fullVideo = container.querySelector(".full-video");
    this.playButton = container.querySelector(".play-button");
    this.playOverlay = container.querySelector(".play-overlay");
    this.videoControls = container.querySelector(".video-controls");
    this.muteBtn = container.querySelector(".control-btn");
    this.muteIcon = container.querySelector("#muteIcon");
    this.unmuteIcon = container.querySelector("#unmuteIcon");
    this.loadingIndicator = container.querySelector(".loading-indicator");

    // State
    this.fullVideoLoaded = false;
    this.activeVideo = this.previewVideo;

    // Register instance
    VideoPlayer.instances.push(this);

    // Initialize
    this.initialize();
  }

  initialize() {
    // Setup event listeners
    this.playButton.addEventListener("click", () => this.loadFullVideo());
    this.playOverlay.addEventListener("click", () => this.loadFullVideo());
    this.muteBtn.addEventListener("click", () => this.toggleMute());

    // Add play event listeners to both videos
    this.previewVideo.addEventListener("play", () => this.handlePlay());
    this.fullVideo.addEventListener("play", () => this.handlePlay());

    // Initialize preview video
    this.previewVideo.load();
    this.setupAutoplay();
    this.setupIntersectionObserver();
  }

  handlePlay() {
    // Mute all other video players
    VideoPlayer.instances.forEach((instance) => {
      if (instance !== this) {
        instance.activeVideo.muted = true;
        instance.updateMuteIcons();
      }
    });
  }

  setupAutoplay() {
    this.previewVideo.addEventListener("canplay", () => {
      this.previewVideo.play().catch(() => {});
    });
  }

  loadFullVideo() {
    if (!this.fullVideoLoaded) {
      this.showLoading();
      this.fullVideo.load();

      this.fullVideo.addEventListener(
        "canplay",
        () => {
          this.hideLoading();
          this.switchToFullVideo();
          this.fullVideoLoaded = true;
          this.fullVideo.muted = false;
          this.updateMuteIcons();
          this.fullVideo.play().catch(() => {});
        },
        { once: true }
      );
    } else {
      this.togglePlayPause();
    }
  }

  showLoading() {
    this.loadingIndicator.style.display = "block";
    this.playButton.style.display = "none";
  }

  hideLoading() {
    this.loadingIndicator.style.display = "none";
  }

  switchToFullVideo() {
    this.previewVideo.style.display = "none";
    this.fullVideo.style.display = "block";
    this.activeVideo = this.fullVideo;
    this.videoControls.style.display = "flex";
  }

  togglePlayPause() {
    if (this.activeVideo.paused) {
      this.activeVideo.play();
      this.playOverlay.style.display = "none";
    } else {
      this.activeVideo.pause();
      this.playOverlay.style.display = "flex";
    }
  }

  toggleMute() {
    this.activeVideo.muted = !this.activeVideo.muted;
    this.updateMuteIcons();

    // If user is unmuting, mute other videos
    if (!this.activeVideo.muted) {
      this.handlePlay();
    }
  }

  updateMuteIcons() {
    this.muteIcon.style.display = this.activeVideo.muted ? "none" : "block";
    this.unmuteIcon.style.display = this.activeVideo.muted ? "block" : "none";
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target.classList.contains("preview-video")
            ? this.previewVideo
            : this.fullVideo;

          if (!entry.isIntersecting && !video.paused) {
            video.pause();
            if (video === this.fullVideo && this.fullVideoLoaded) {
              this.playOverlay.style.display = "flex";
            }
          } else if (
            entry.isIntersecting &&
            video.paused &&
            (video === this.previewVideo || this.fullVideoLoaded)
          ) {
            video.play().catch(() => {});
            if (video === this.fullVideo) {
              this.playOverlay.style.display = "none";
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(this.previewVideo);
    observer.observe(this.fullVideo);
  }
}

// Initialize all video containers
document.querySelectorAll(".video-container").forEach((container) => {
  new VideoPlayer(container);
});
